import React from 'react';
import PropTypes from 'prop-types';
import { Checkbox, Radio } from 'antd';

import buildRecordViewerPath from '../../util/buildRecordViewerPath';
import { formatPhoneNumber } from '../../util/phoneNumber';
import {
  DOCUMENT_OPTION, LINE_OPTION, canRemember, currentNumber,
} from './replyFaxApi';

import './replyFax.scss';

// Picks where a referral's replies are faxed: the number the fax states or the
// line's current reply number. `compact` drops the quote for tight spaces like the
// Update Referral panel.
const ReplyFaxNumberChoice = ({
  destination,
  sendToken,
  value,
  onChange,
  compact,
  disabled,
}) => {
  const {
    extracted_number: extractedNumber,
    sender_fax_number: senderFaxNumber,
    evidence,
    file_token: fileToken,
  } = destination;
  const lineNumber = currentNumber(destination);
  const lineIsOverride = Boolean(lineNumber && senderFaxNumber && lineNumber !== senderFaxNumber);
  const sameNumber = extractedNumber && extractedNumber === lineNumber;
  const pageNumber = evidence?.page_number;
  const quote = evidence?.supporting_quote;
  const rememberable = canRemember(destination);

  const pageLink = pageNumber && fileToken ? (
    <a
      href={buildRecordViewerPath({ file_token: fileToken, send_token: sendToken }, { mode: 'referred', page: pageNumber })}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(event) => event.stopPropagation()}
    >
      {`page ${pageNumber}`}
    </a>
  ) : null;

  return (
    <div className={`reply-fax-choice${compact ? ' reply-fax-choice--compact' : ''}`}>
      <Radio.Group
        value={value.option}
        disabled={disabled}
        onChange={(event) => onChange({ ...value, option: event.target.value })}
      >
        {extractedNumber && (
          <Radio value={DOCUMENT_OPTION} className="reply-fax-choice__option" data-testid="reply-fax-choice-document">
            <span className="reply-fax-choice__number">{formatPhoneNumber(extractedNumber)}</span>
            <span className="reply-fax-choice__source">
              {sameNumber && !lineIsOverride && ' · on the fax and the line it came from'}
              {sameNumber && lineIsOverride && ' · on the fax and set up for this line'}
              {!sameNumber && ' · number on the fax'}
              {compact && pageLink && (
                <>
                  {', '}
                  {pageLink}
                </>
              )}
            </span>
            {!compact && quote && (
              <span className="reply-fax-choice__quote">
                {`“${quote}”`}
                {pageLink && (
                  <>
                    {' · '}
                    {pageLink}
                  </>
                )}
              </span>
            )}
          </Radio>
        )}
        {lineNumber && !sameNumber && (
          <Radio value={LINE_OPTION} className="reply-fax-choice__option" data-testid="reply-fax-choice-line">
            <span className="reply-fax-choice__number">{formatPhoneNumber(lineNumber)}</span>
            <span className="reply-fax-choice__source">
              {lineIsOverride ? ' · set up for this line' : ' · line the fax came from'}
            </span>
          </Radio>
        )}
      </Radio.Group>
      {rememberable && value.option === DOCUMENT_OPTION && (
        // Ticked saves the number for the line (ApprovedReplyFaxDestination), which
        // routes automatic replies, so the note says what each state does.
        <div className="reply-fax-choice__remember-group">
          <Checkbox
            className="reply-fax-choice__remember"
            checked={value.remember}
            disabled={disabled}
            onChange={(event) => onChange({ ...value, remember: event.target.checked })}
            data-testid="reply-fax-choice-remember"
          >
            Use this number for future faxes from this line
          </Checkbox>
          <div className="reply-fax-choice__remember-help" data-testid="reply-fax-choice-remember-help">
            {value.remember
              ? `This referral and any incoming fax from ${formatPhoneNumber(senderFaxNumber)} that lists this number will automatically reply to ${formatPhoneNumber(extractedNumber)}. Uncheck to use it for this referral only.`
              : `Right now, only this referral will reply to ${formatPhoneNumber(extractedNumber)}. If you check this box, any incoming fax from ${formatPhoneNumber(senderFaxNumber)} that lists this number will automatically use it.`}
          </div>
        </div>
      )}
    </div>
  );
};

ReplyFaxNumberChoice.propTypes = {
  destination: PropTypes.object.isRequired,
  sendToken: PropTypes.string,
  value: PropTypes.shape({
    option: PropTypes.string,
    remember: PropTypes.bool,
  }).isRequired,
  onChange: PropTypes.func.isRequired,
  compact: PropTypes.bool,
  disabled: PropTypes.bool,
};

ReplyFaxNumberChoice.defaultProps = {
  sendToken: null,
  compact: false,
  disabled: false,
};

export default ReplyFaxNumberChoice;
